import React, { useState, useMemo } from 'react';
import { Select } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_ALL_CLIENTS } from '../../graphql/queries/settlement/getSettlementBillQueries';
import { GET_SETTLEMENT_CLIENT_BY_ROLE, GetSettlementClientByRoleResponse } from '../../graphql/queries/settlement/getSettlementClientByRole';
import type { ClientSuggestionType } from '../../graphql/types/settlement';

const { Option } = Select;

interface ClientSugSelectorProps {
  /** 单选时为 string，多选时为 string[] */
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowClear?: boolean;
  /** 按结算客户角色过滤：0=订单客户，1=DSP；不传则不过滤 */
  roleType?: number;
  /** 多选；为 true 时 value/onChange 为数组 */
  mode?: 'single' | 'multiple';
}

/**
 * 客户建议选择器组件
 * 基于现有 PickupAppointmentDrawer 中的客户选择器实现
 * 使用前端搜索，提升用户体验
 */
/** 将按 roleType 查询的 SelectOption 转为 ClientSuggestionType 形状 */
function mapOptionToClient(opt: { value: string; label: string }): ClientSuggestionType {
  return {
    clientId: opt.value,
    clientName: opt.label,
    displayName: opt.label
  };
}

export const ClientSugSelector: React.FC<ClientSugSelectorProps> = ({
  value,
  onChange,
  placeholder = "Search Customer...",
  className = "",
  disabled = false,
  allowClear = true,
  roleType,
  mode = 'single'
}) => {
  const [searchText, setSearchText] = useState<string>('');
  const isMultiple = mode === 'multiple';

  const useRoleQuery = roleType !== undefined;

  const { data: clientData, loading: clientLoading } = useQuery(GET_ALL_CLIENTS, {
    fetchPolicy: 'cache-and-network',
    skip: useRoleQuery
  });

  const { data: roleData, loading: roleLoading } = useQuery<GetSettlementClientByRoleResponse>(
    GET_SETTLEMENT_CLIENT_BY_ROLE,
    {
      fetchPolicy: 'cache-and-network',
      skip: !useRoleQuery,
      variables: { roleType: useRoleQuery ? roleType : undefined }
    }
  );

  const allClients: ClientSuggestionType[] = useRoleQuery
    ? (roleData?.getAllSettlementClient || []).map(mapOptionToClient)
    : (clientData?.getAllClients || []);
  const loading = useRoleQuery ? roleLoading : clientLoading;

  // 前端搜索过滤
  const filteredClients = useMemo(() => {
    if (!searchText.trim()) {
      return allClients;
    }
    
    const keyword = searchText.toLowerCase();
    return allClients.filter((client: ClientSuggestionType) => 
      client.clientName.toLowerCase().includes(keyword) ||
      client.clientId.toLowerCase().includes(keyword) ||
      client.displayName.toLowerCase().includes(keyword)
    );
  }, [allClients, searchText]);

  // 处理客户搜索
  const handleClientSearch = (value: string) => {
    setSearchText(value);
  };

  // 处理客户选择
  const handleClientChange = (val: string | string[]) => {
    if (onChange) {
      onChange(val);
    }
  };

  // 每次打开下拉框时重置搜索关键字，展示全量选项（含已勾选项），而不是停留在上次搜索的过滤结果里
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setSearchText('');
    }
  };

  // 处理清除选择
  const handleClear = () => {
    setSearchText('');
    if (onChange) {
      onChange(isMultiple ? [] : '');
    }
  };

  // 获取显示名称
  const getDisplayName = (client: ClientSuggestionType) => {
    return client.displayName || `${client.clientName} (${client.clientId})`;
  };

  const selectValue = isMultiple
    ? (Array.isArray(value) ? value : value ? [value] : [])
    : (Array.isArray(value) ? value[0] : value);

  return (
    <Select
      className={className}
      placeholder={placeholder}
      loading={loading}
      notFoundContent={loading ? 'Loading...' : 'No Customer'}
      showSearch
      filterOption={false}
      onSearch={handleClientSearch}
      onChange={handleClientChange}
      allowClear={allowClear}
      onClear={handleClear}
      onOpenChange={handleOpenChange}
      disabled={disabled}
      value={selectValue}
      mode={isMultiple ? 'multiple' : undefined}
      maxTagCount={isMultiple ? 'responsive' : undefined}
      dropdownStyle={{
        maxHeight: 300,
        overflow: 'auto'
      }}
    >
      {filteredClients.map((client: ClientSuggestionType) => (
        <Option key={client.clientId} value={client.clientId}>
          {getDisplayName(client)}
        </Option>
      ))}
    </Select>
  );
};
